import type { ReactNode } from "react";

import { Card, type CardProps } from "@/design-system";

import type { AppShell } from "@/lib/navigation";
import { ShellBrand } from "./ShellBrand";

interface LoginCardProps {
  shell: AppShell;
  children: ReactNode;
  title?: string;
  subtitle?: string;
  footer?: ReactNode;
  cardProps?: Partial<CardProps>;
  /**
   * Embedded mode renders only the card payload (for use inside
   * existing auth shells such as ``AuthShell``).
   */
  embedded?: boolean;
}

export function LoginCard({
  shell,
  children,
  title,
  subtitle,
  footer,
  cardProps,
  embedded = false,
}: LoginCardProps) {
  const heading = title ?? "Welcome back";

  const card = (
    <Card
      variant="default"
      className="relative z-10 w-full max-w-sm border border-border-subtle bg-raised p-8"
      {...cardProps}
    >
      <div className="mb-6 flex items-center justify-center">
        <ShellBrand shell={shell} compact />
      </div>

      <h1 className="text-center text-xl font-semibold tracking-tight text-text-primary">
        {heading}
      </h1>
      {subtitle ? (
        <p className="mt-1 text-center text-sm text-text-secondary">{subtitle}</p>
      ) : null}

      <div className="mt-6 flex flex-col gap-4">{children}</div>

      {footer ? <div className="relative z-10 mt-6 max-w-sm">{footer}</div> : null}
    </Card>
  );

  if (embedded) {
    return card;
  }

  return (
    <div
      className="relative flex min-h-screen items-center justify-center bg-canvas px-4 py-10"
      data-testid="login-card-shell"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 overflow-hidden"
      >
        {shell === "shortpay" ? (
          <div className="absolute inset-0 bg-gradient-to-b from-sage/10 via-transparent to-emerald/10" />
        ) : (
          <>
            <div className="absolute -left-32 -top-32 h-[420px] w-[420px] rounded-full bg-teal-tint opacity-50 blur-3xl" />
            <div className="absolute -bottom-40 -right-32 h-[480px] w-[480px] rounded-full bg-sage-tint opacity-40 blur-3xl" />
          </>
        )}
      </div>

      {card}
    </div>
  );
}
